import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ValuesDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskStat,
    DeskEmpty,
    VALUES_TINT,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
} from '../../../components/staff/values/ValuesDesk';
import {
    FurniThumb,
    formatNumber,
    relativeFromMinutes,
    ageTone,
    TONE_TEXT,
    TrendPill,
} from '../../../components/staff/values/valuesShared';
import { getValuesOverview, dismissValueReview, clearValueReview } from '../../../services/api';

const STALE_OPTIONS = [
    { value: 30, label: '30 days' },
    { value: 60, label: '60 days' },
    { value: 90, label: '90 days' },
    { value: 180, label: '6 months' },
    { value: 365, label: '1 year' },
];

const STALE_KEY = 'franklyValuesStaleDays';

const readStaleDays = () => {
    try {
        const stored = parseInt(localStorage.getItem(STALE_KEY), 10);
        if (STALE_OPTIONS.some(option => option.value === stored)) return stored;
    } catch {}
    return 90;
};

const DISMISS_OPTIONS = [
    { value: '30', label: 'Hide it for 30 days' },
    { value: '7', label: 'Hide it for 7 days' },
    { value: '90', label: 'Hide it for 90 days' },
    { value: '180', label: 'Hide it for 6 months' },
    { value: 'muted', label: 'Never chase this furni again' },
];

function DismissModal({ item, saving, onCancel, onConfirm }) {
    const [choice, setChoice] = useState('30');

    useEffect(() => {
        setChoice('30');
    }, [item]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel]);

    if (!item) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div
                className="w-full max-w-[420px] border border-gray-300 bg-offwhite"
                style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)' }}
            >
                <header className="px-3 py-[6px]" style={{ backgroundColor: VALUES_TINT }}>
                    <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">
                        Dismiss from the queue
                    </h3>
                </header>

                <div className="flex flex-col gap-3 p-3">
                    <p className="text-[13px] text-gray-700">
                        <strong>{item.name}</strong> will drop off the review list. Setting a value brings it back
                        straight away.
                    </p>

                    <select
                        value={choice}
                        onChange={(e) => setChoice(e.target.value)}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-maroon"
                    >
                        {DISMISS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={onCancel} disabled={saving} className={DESK_BUTTON_CLASS}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => onConfirm(
                                choice === 'muted' ? { muted: true } : { days: Number(choice) }
                            )}
                            className={DESK_PRIMARY_CLASS}
                            style={DESK_PRIMARY_STYLE}
                        >
                            {saving ? 'Hiding' : 'Dismiss'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AttentionRow({ item, staleDays, onUpdate, onDismiss, busy }) {
    const tone = ageTone(item.daysSince, staleDays);

    return (
        <tr className="border-b border-gray-200 transition-colors hover:bg-gray-50">
            <td className="px-3 py-2">
                <div className="flex items-center gap-3">
                    <FurniThumb src={item.iconImg || item.smallImg} alt={item.name} size={44} />
                    <div className="min-w-0">
                        <div className="truncate text-[13px] font-bold text-gray-900">{item.name}</div>
                        <div className="truncate text-[11px] text-gray-400">{item.code}</div>
                    </div>
                </div>
            </td>
            <td className="hidden px-3 py-2 text-[12px] text-gray-600 sm:table-cell">{item.categoryName}</td>
            <td className="whitespace-nowrap px-3 py-2 text-[12px]">
                {item.neverValued ? (
                    <span className="font-bold text-red-600">No value yet</span>
                ) : (
                    <span className="font-bold text-gray-900">
                        {formatNumber(item.min)} - {formatNumber(item.max)}
                    </span>
                )}
            </td>
            <td className={'whitespace-nowrap px-3 py-2 text-[12px] font-bold ' + TONE_TEXT[tone]}>
                {item.neverValued ? 'Never updated' : relativeFromMinutes(item.minutesSince)}
            </td>
            <td className="px-3 py-2">
                <div className="flex flex-wrap items-center justify-end gap-1">
                    <button
                        type="button"
                        onClick={() => onDismiss(item)}
                        disabled={busy}
                        className={DESK_BUTTON_CLASS + ' disabled:opacity-40'}
                        title="Hide this furni from the review queue"
                    >
                        Dismiss
                    </button>
                    <button
                        type="button"
                        onClick={() => onUpdate(item)}
                        className={DESK_PRIMARY_CLASS}
                        style={DESK_PRIMARY_STYLE}
                    >
                        Update
                    </button>
                </div>
            </td>
        </tr>
    );
}

function FeedRow({ entry, onOpen }) {
    const delta = entry.previousAverage === null ? null : entry.average - entry.previousAverage;

    const movement = entry.previousAverage === null
        ? 'Set to ' + formatNumber(entry.min) + ' - ' + formatNumber(entry.max)
        : formatNumber(entry.previousMin) + ' - ' + formatNumber(entry.previousMax)
            + ' → ' + formatNumber(entry.min) + ' - ' + formatNumber(entry.max);

    return (
        <li className="flex items-center gap-3 border-b border-gray-200 px-3 py-2 last:border-b-0">
            <FurniThumb src={entry.iconImg || entry.smallImg} alt={entry.name} size={40} />

            <div className="min-w-0 flex-1">
                <button
                    type="button"
                    onClick={() => onOpen(entry)}
                    className="block max-w-full truncate text-left text-[13px] font-bold text-gray-900 hover:text-maroon"
                >
                    {entry.name}
                </button>
                <div className="text-[11px] text-gray-500">{movement}</div>
                <div className="text-[11px] text-gray-400">
                    {entry.updatedBy} · {relativeFromMinutes(entry.minutesSince)}
                </div>
            </div>

            <TrendPill trend={entry.trend} delta={delta} />
        </li>
    );
}

function DismissedRow({ item, onRestore, busy }) {
    const by = item.dismissedBy ? ' · hidden by ' + item.dismissedBy : '';
    const reason = item.muted ? 'Never chased' + by : 'Back on ' + item.snoozedUntilText + by;

    return (
        <li className="flex items-center gap-3 border-b border-gray-200 px-3 py-2 last:border-b-0">
            <FurniThumb src={item.iconImg || item.smallImg} alt={item.name} size={36} />

            <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] text-gray-900">{item.name}</div>
                <div className="text-[11px] text-gray-500">{reason}</div>
                {item.dismissNote && <div className="text-[11px] italic text-gray-400">{item.dismissNote}</div>}
            </div>

            <button
                type="button"
                onClick={() => onRestore(item)}
                disabled={busy}
                className={DESK_BUTTON_CLASS + ' disabled:opacity-40'}
            >
                Restore
            </button>
        </li>
    );
}

function Overview({ userData }) {
    const navigate = useNavigate();
    const [staleDays, setStaleDays] = useState(readStaleDays);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [dismissTarget, setDismissTarget] = useState(null);

    const load = useCallback(async (days, { quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const response = await getValuesOverview({ staleDays: days, limit: 40, feedLimit: 25 });
            if (response.success) {
                setData(response);
            } else {
                toast.error(response.message || 'Failed to load the values overview');
            }
        } catch {
            toast.error('Failed to load the values overview');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(staleDays);
    }, [load, staleDays]);

    if (!userData?.hasStaffRole) return <Navigate to="/" replace />;

    const changeStaleDays = (days) => {
        setStaleDays(days);
        try { localStorage.setItem(STALE_KEY, String(days)); } catch {}
    };

    const openItem = (item) => {
        navigate('/staff/values/furni?item=' + encodeURIComponent(item.furniId || item.id));
    };

    const handleDismiss = async (payload) => {
        const item = dismissTarget;
        if (!item) return;

        setBusyId(item.id);
        try {
            const response = await dismissValueReview(item.id, payload);
            if (response.success) {
                toast.success(response.message);
                setDismissTarget(null);
                load(staleDays, { quiet: true });
            } else {
                toast.error(response.message || 'Failed to dismiss that furni');
            }
        } catch {
            toast.error('Failed to dismiss that furni');
        } finally {
            setBusyId(null);
        }
    };

    const handleRestore = async (item) => {
        setBusyId(item.id);
        try {
            const response = await clearValueReview(item.id);
            if (response.success) {
                toast.success(response.message);
                load(staleDays, { quiet: true });
            } else {
                toast.error(response.message || 'Failed to restore that furni');
            }
        } catch {
            toast.error('Failed to restore that furni');
        } finally {
            setBusyId(null);
        }
    };

    const stats = data?.stats;
    const coverage = stats && stats.releasedItems > 0
        ? Math.round((stats.valuedItems / stats.releasedItems) * 100)
        : 0;

    return (
        <ValuesDesk userData={userData}>
            <DismissModal
                item={dismissTarget}
                saving={busyId === dismissTarget?.id}
                onCancel={() => setDismissTarget(null)}
                onConfirm={handleDismiss}
            />

            <DeskRibbon>
                <DeskTitle>Values Overview</DeskTitle>
                <DeskNote>What needs doing, and what has just been done</DeskNote>

                <div className="flex-1" />

                <label className="flex items-center gap-2">
                    <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                        Chase after
                    </span>
                    <select
                        value={staleDays}
                        onChange={(e) => changeStaleDays(Number(e.target.value))}
                        className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-maroon"
                    >
                        {STALE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                <button type="button" onClick={() => load(staleDays)} className={DESK_BUTTON_CLASS}>
                    Refresh
                </button>
            </DeskRibbon>

            {loading && !data ? (
                <DeskPanel title="Loading">
                    <div className="space-y-2">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="h-10 animate-pulse bg-gray-100" />
                        ))}
                    </div>
                </DeskPanel>
            ) : !data ? (
                <DeskPanel title="Values Overview">
                    <DeskEmpty>Nothing loaded, try refresh</DeskEmpty>
                </DeskPanel>
            ) : (
                <>
                    <DeskPanel title="At a glance" padding="p-3">
                        <div className="flex flex-wrap gap-2">
                            <DeskStat
                                label="Needs a look"
                                value={formatNumber(stats.staleItems)}
                                tone={stats.staleItems > 0 ? 'warn' : 'good'}
                                hint={'No update in ' + staleDays + ' days'}
                            />
                            <DeskStat
                                label="Never valued"
                                value={formatNumber(stats.neverValued)}
                                tone={stats.neverValued > 0 ? 'bad' : 'good'}
                                hint="Released, no value set"
                            />
                            <DeskStat
                                label="Coverage"
                                value={coverage + '%'}
                                tone={coverage >= 90 ? 'good' : coverage >= 70 ? 'warn' : 'bad'}
                                hint={formatNumber(stats.valuedItems) + ' of ' + formatNumber(stats.releasedItems) + ' released'}
                            />
                            <DeskStat label="Updated (7d)" value={formatNumber(stats.updates7)} hint="Values set this week" />
                            <DeskStat label="Updated (30d)" value={formatNumber(stats.updates30)} hint="Values set this month" />
                            <DeskStat label="Dismissed" value={formatNumber(stats.dismissedItems)} hint="Hidden from the queue" />
                            <DeskStat label="Categories" value={formatNumber(stats.categories)} hint="Value categories" />
                        </div>

                        {data.contributors.length > 0 && (
                            <p className="mt-3 text-[12px] text-gray-500">
                                Busiest this month:{' '}
                                {data.contributors.map((contributor, index) => (
                                    <span key={contributor.name}>
                                        {index > 0 && ', '}
                                        <strong className="text-gray-700">{contributor.name}</strong> ({contributor.total})
                                    </span>
                                ))}
                            </p>
                        )}
                    </DeskPanel>

                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                        <div className="flex flex-col gap-3 xl:col-span-2">
                            <DeskPanel
                                title={'Needs a value update (' + data.attention.length + ')'}
                                padding="p-0"
                                right={
                                    <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-white/70">
                                        Oldest first
                                    </span>
                                }
                            >
                                {data.attention.length === 0 ? (
                                    <DeskEmpty>Nothing to chase, every released furni is current</DeskEmpty>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b-2 border-gray-300 bg-gray-100">
                                                    <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Furni</th>
                                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Category</th>
                                                    <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Current</th>
                                                    <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Last set</th>
                                                    <th className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.attention.map(item => (
                                                    <AttentionRow
                                                        key={item.id}
                                                        item={item}
                                                        staleDays={staleDays}
                                                        onUpdate={openItem}
                                                        onDismiss={setDismissTarget}
                                                        busy={busyId === item.id}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </DeskPanel>

                            {data.dismissed.length > 0 && (
                                <DeskPanel title={'Dismissed (' + data.dismissed.length + ')'} padding="p-0" tint="#6b7280">
                                    <ul>
                                        {data.dismissed.map(item => (
                                            <DismissedRow
                                                key={item.id}
                                                item={item}
                                                onRestore={handleRestore}
                                                busy={busyId === item.id}
                                            />
                                        ))}
                                    </ul>
                                </DeskPanel>
                            )}
                        </div>

                        <DeskPanel title="Recent updates" padding="p-0">
                            {data.feed.length === 0 ? (
                                <DeskEmpty>No values have been set yet</DeskEmpty>
                            ) : (
                                <ul>
                                    {data.feed.map(entry => (
                                        <FeedRow key={entry.valueId} entry={entry} onOpen={openItem} />
                                    ))}
                                </ul>
                            )}
                        </DeskPanel>
                    </div>
                </>
            )}
        </ValuesDesk>
    );
}

export default Overview;
